import React, { useRef } from 'react';
import { ApiServerEntityIDType } from '@cp/utils/apiServer/types';
import { Stack } from '@cp/ds/src/components/stack';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { useResumeData } from 'src/entities/resume';
import { ClientPhotoPurpose } from '@common/model/client/photo/purpose';
import { SubjectModalView } from 'src/shared/ui/subjectModalView';
import { ModerationUpdateForResumeButton } from 'src/features/moderation/updateForResume';
import { useClientPhotoEditingCreator } from 'src/features/client/photo';
import { ResumeAboutMeEditableSection } from 'src/features/resume/edit/ui/aboutMeSection';
import { ClientPersonalInformationEditableSection } from 'src/features/client/personalInformationEdit';
import { ResumeScheduleEditableSection } from 'src/features/resume/edit/ui/scheduleSection';
import { ResumeOptionsEditableSection, ResumeTariffsEditableSection } from 'src/features/resume/edit';
import { ResumeAdditionalEditableSection } from 'src/features/resume/edit/ui/additionalSection';
import { ClientContactsEditableSection } from 'src/features/client/contactsEdit';
import { DialogViewButton } from 'src/features/dialog/view';
import { ClientDeleteButton } from 'src/features/client/delete';
import { ClientFeedbacksEditableSection } from 'src/features/client/feedbacks';
import { ClientAudioSection } from 'src/entities/clientAudio';
import { PersonalQualitiesSection } from 'src/features/client/personalQualities';
import { useAiImageGeneratorModal } from 'src/features/aigenerator';
import { useClientImpersonation } from 'src/features/client/impersonate';
import { BotIdentificator } from '@common/model/bot';
import { MemberUser } from '@youhelp-ru/core';
import { CAREER_IDS_WITH_ENABLED_ADDITIONAL } from '@common/const/catalog/item';
import { SpecialistPhotosSection } from 'src/widgets/profileEditor/ui/specialistPhotosSection';
import { IResumeViewHandle, ResumeViewProps } from './model';

export * from './model';

export const ResumeView = React.forwardRef<IResumeViewHandle, ResumeViewProps>((props, ref) => {
  const theme = useTheme();
  const [resumeId, setResumeId] = React.useState<ApiServerEntityIDType | null>(null);
  const { resume, isLoadingResume, refetch } = useResumeData({ resumeId });
  const et = useRef(new EventTarget());
  const createPhotoEditHandler = useClientPhotoEditingCreator();
  const { openAiImageGeneratorModal } = useAiImageGeneratorModal();
  const { openAsClient, isLoading: isImpersonateLoading } = useClientImpersonation();

  const handleClick = () => {
    if (!resume?.clientProfile) {
      return;
    }

    const mainPhoto = resume.clientProfile.photoSet?.find((p) => p.purpose === ClientPhotoPurpose.Resume || p.isMain);
    const originalImageUrl = resume.clientProfile.mainPhoto?.notCroppedOriginalSrc || mainPhoto?.image?.base?.src;

    openAiImageGeneratorModal({
      clientId: resume.clientProfile.id,
      resumeId: resumeId || undefined,
      originalImageUrl,
      onPhotoSaved: refetch,
    });
  };

  const hide = React.useCallback(() => {
    setResumeId(null);
    et.current.dispatchEvent(new CustomEvent('close'));
  }, []);

  React.useImperativeHandle(
    ref,
    () => ({
      view: (id: ApiServerEntityIDType, onClose) => {
        setResumeId(id);
        if (onClose) {
          et.current.addEventListener('close', () => onClose(), { once: true });
        }
      },
      hide,
    }),
    [hide],
  );

  const dataForClientPhotoAvatar = {
    ...resume?.clientProfile,
    mainPhoto: resume?.clientProfile?.photoSet?.find((p) => p.purpose === ClientPhotoPurpose.Avatar),
  };

  const dataForClientPhotoResume = {
    ...resume?.clientProfile,
    mainPhoto: resume?.clientProfile?.photoSet?.find((p) => p.purpose === ClientPhotoPurpose.Resume),
  };

  return (
    <SubjectModalView
      buttons={
        <Stack alignItems="center" direction="row" gap={2}>
          <ModerationUpdateForResumeButton resumeId={resumeId} />
          <DialogViewButton
            color="white"
            dialogFindQuery={
              resume?.clientProfile ? { as: MemberUser.bot(BotIdentificator.SupportBot), clientId: resume.clientProfile.id } : undefined
            }
            sx={{ border: `1px solid ${theme.palette['gray light']}` }}
            variant="primary"
          />
          <ClientDeleteButton clientId={resume?.clientProfile.id} color="white" />
        </Stack>
      }
      isOpen={Boolean(resumeId)}
      onClose={hide}
    >
      <Stack direction="column" gap={2.5} sx={{ pb: 5 }}>
        <SpecialistPhotosSection
          avatarPhotoData={dataForClientPhotoAvatar}
          clientProfile={resume?.clientProfile}
          isImpersonateLoading={isImpersonateLoading}
          onAiGenerateClick={handleClick}
          onAvatarPhotoClick={() => createPhotoEditHandler({ clientProfile: resume?.clientProfile, purpose: ClientPhotoPurpose.Avatar })}
          onImpersonateClick={resume?.clientProfile?.id ? () => openAsClient(resume.clientProfile.id) : undefined}
          onResumePhotoClick={() => createPhotoEditHandler({ clientProfile: resume?.clientProfile, purpose: ClientPhotoPurpose.Resume })}
          resumePhotoData={dataForClientPhotoResume}
        />
        <ClientPersonalInformationEditableSection clientProfile={resume?.clientProfile} isLoading={isLoadingResume} />
        {resume?.clientProfile?.id ? <PersonalQualitiesSection clientId={resume.clientProfile.id} /> : null}
        <ClientAudioSection clientId={resume?.clientProfile.id} sx={{ mb: 2.5 }} />
        <ResumeAboutMeEditableSection resumeId={resumeId} />
        <ResumeOptionsEditableSection resumeId={resumeId} />
        <ResumeScheduleEditableSection resumeId={resumeId} />
        <ResumeTariffsEditableSection resumeId={resumeId} />
        {CAREER_IDS_WITH_ENABLED_ADDITIONAL.includes(resume?.careerId || 0) && <ResumeAdditionalEditableSection resumeId={resumeId} />}
        <ClientContactsEditableSection clientId={resume?.clientProfile.id} />
        <ClientFeedbacksEditableSection clientId={resume?.clientProfile.id} mainResumeId={resumeId} />
      </Stack>
    </SubjectModalView>
  );
});
